import Breadcrumbs from "@/components/Breadcrumb";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";
import { cn } from "@/components/podkit/lib/cn";
import type { BreadcrumbItem } from "@/contexts/SettingsPageContext";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

export type SubPageHeaderProps = {
    /**
     * Breadcrumb items to display. If provided, renders the Breadcrumbs component.
     */
    breadcrumbs?: BreadcrumbItem[];
    /**
     * Action buttons or other content to display on the right side.
     */
    actions?: ReactNode;
    /**
     * Custom content to render instead of breadcrumbs. Use for custom header content.
     */
    children?: ReactNode;

    loading?: { breadcrumbItemCount: number  }
} & Omit<HTMLAttributes<HTMLDivElement>, "children">;

/**
 * A page header component that displays breadcrumb navigation and action buttons.
 */
export const SubPageHeader = forwardRef<HTMLDivElement, SubPageHeaderProps>(
    ({ breadcrumbs, actions, loading, children, className, ...props }, ref) => {
        return (
            <header
                ref={ref}
                className={cn(
                    "@container/page-header",
                    "flex items-center justify-between gap-4",
                    "bg-surface-primary",
                    "border-b border-border-base",
                    "px-6 py-3",
                    className,
                )}
                {...props}
            >
                <div className="min-w-[min(67%,200px)]">
                    {loading?.breadcrumbItemCount ? <BreadcrumbsSkeleton itemsCount={2} /> : null }
                    {children ?? (breadcrumbs && <Breadcrumbs customBreadcrumbs={breadcrumbs} />)}
                </div>
                {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
            </header>
        );
    },
);
SubPageHeader.displayName = "PageHeader";
